import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useReportStore } from '../../store/reports/reportStore';
import useSchoolStore from '../../store/schoolStore';
import useStudentStore from '../../store/studentStore';
import { getClassName } from '../../utils/schoolUtils';
import { LuArrowLeft, LuFileX, LuGraduationCap, LuRefreshCw, LuUsers } from 'react-icons/lu';
import PageHeader from '../../components/common/PageHeader';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Notice from '../../components/common/Notice';
import EmptyState from '../../components/common/EmptyState';
import LoadingMessage from '../../components/common/LoadingMessage';
import { Field, SearchInput, Select } from '../../components/common/FormControls';

// Subcomponents
import StudentReportCard from '../../components/reports/student/StudentReportCard';
import StudentReportHeader from '../../components/reports/student/StudentReportHeader';
import StudentReportWarningBanner from '../../components/reports/student/StudentReportWarningBanner';
import StudentReportSummaryCard from '../../components/reports/student/StudentReportSummaryCard';
import StudentReportPerformanceOverview from '../../components/reports/student/StudentReportPerformanceOverview';
import StudentReportSubjectsList from '../../components/reports/student/StudentReportSubjectsList';
import StudentReportRecommendationsCard from '../../components/reports/student/StudentReportRecommendationsCard';

export default function StudentReportPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const routeParams = useParams();

    // Stores
    const { 
        schoolId: storeSchoolId, 
        classes, 
        getClasses, 
        isClassesLoading,
        schoolData,
        currentAcademicYear: storeAcademicYear 
    } = useSchoolStore();
    
    const schoolId = routeParams.schoolId || searchParams.get('schoolId') || storeSchoolId;
    const academicYear = storeAcademicYear || schoolData?.currentAcademicYear || '';

    const { 
        selectedStudent,
        setSelectedStudent,
        studentReport, 
        studentReportsMap,
        isLoadingStudentReport, 
        isLoadingStudentReportsList,
        studentReportsListError,
        getStudentReports,
        getStudentReport,
        setSelectedStudentReport,
        clearStudentReport
    } = useReportStore();

    const { 
        students, 
        isStudentsLoading, 
        getStudentsOfClass
    } = useStudentStore();

    // Local Dropdown & Selection State
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedSec, setSelectedSec] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Initial load: Fetch classes
    useEffect(() => {
        if (schoolId) {
            getClasses();
        }
    }, [schoolId, getClasses]);

    // Available sections for the currently selected class
    const availableSections = useMemo(() => {
        if (!selectedClass || !classes) return [];
        const classObj = classes.find(c => String(c.Class) === String(selectedClass));
        if (!classObj || !classObj.sections) return [];
        return classObj.sections.map(s => (typeof s === 'object' ? s.sec : s)).filter(Boolean);
    }, [selectedClass, classes]);

    // Handle Class and Section data loading
    const loadClassStudentsAndReports = useCallback(async (classVal, secVal) => {
        if (!schoolId || !classVal || !secVal) return;

        // Fetch students and student reports in parallel with academicYear filter
        await Promise.all([
            getStudentsOfClass(schoolId, classVal, secVal),
            getStudentReports({ 
                schoolId, 
                Class: classVal, 
                sec: secVal, 
                academicYear 
            })
        ]);
    }, [schoolId, academicYear, getStudentsOfClass, getStudentReports]);

    // Handle Class Change
    const handleClassChange = (e) => {
        const val = e.target.value;
        setSelectedClass(val);
        setSelectedSec('');
        setSelectedStudent(null);
        clearStudentReport();

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (val) {
            newParams.set('class', val);
        } else {
            newParams.delete('class');
        }
        newParams.delete('sec');
        setSearchParams(newParams);
    };

    // Handle Section Change
    const handleSectionChange = (e) => {
        const val = e.target.value;
        setSelectedSec(val);
        setSelectedStudent(null);
        clearStudentReport();

        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (val) {
            newParams.set('sec', val);
        } else {
            newParams.delete('sec');
        }
        setSearchParams(newParams);

        if (selectedClass && val) {
            loadClassStudentsAndReports(selectedClass, val);
        }
    };

    // Load initial values from URL params if present
    useEffect(() => {
        const urlClass = searchParams.get('class');
        const urlSec = searchParams.get('sec');

        if (urlClass && urlClass !== selectedClass) {
            setSelectedClass(urlClass);
        }
        if (urlSec && urlSec !== selectedSec) {
            setSelectedSec(urlSec);
        }

        if (urlClass && urlSec && schoolId) {
            loadClassStudentsAndReports(urlClass, urlSec);
        }
    }, [searchParams, schoolId, loadClassStudentsAndReports]); // eslint-disable-line react-hooks/exhaustive-deps

    // Manual Refresh of the active class & section
    const handleRefreshClassData = () => {
        if (selectedClass && selectedSec) {
            loadClassStudentsAndReports(selectedClass, selectedSec);
        }
    };

    // Handle Selecting a Student to view detailed report
    const handleSelectStudent = (student, report) => {
        setSelectedStudent(student);
        setSelectedStudentReport(report || null);
    };

    // Return back to class student list from detail view
    const handleBackToList = () => {
        setSelectedStudent(null);
        clearStudentReport();
    };

    // Handle selecting a subject: Standard route navigation
    const handleSelectSubject = (subject) => {
        navigate(
            `/dash/${schoolId}/student-report/subject?class=${selectedClass}&sec=${selectedSec}&userId=${selectedStudent?.userId || ''}&subject=${encodeURIComponent(subject.subjectName)}`,
            {
                state: {
                    subject,
                    studentName: selectedStudent?.name,
                    selectedClass,
                    selectedSec,
                    academicYear,
                    userId: selectedStudent?.userId
                }
            }
        );
    };

  

    // Client-side filtering of students list
    const filteredStudents = useMemo(() => {
        if (!students || !Array.isArray(students)) return [];
        if (!searchQuery.trim()) return students;
        const q = searchQuery.toLowerCase().trim();
        return students.filter(s => {
            const nameMatch = (s.name || '').toLowerCase().includes(q);
            const admMatch = (s.admissionNo || '').toLowerCase().includes(q);
            const rollMatch = String(s.rollNo || '').toLowerCase().includes(q);
            return nameMatch || admMatch || rollMatch;
        });
    }, [students, searchQuery]);

    // Statistics counts across the class
    const stats = useMemo(() => {
        const total = (students && students.length) || 0;
        let generated = 0;
        let generating = 0;
        let warning = 0;
        let failed = 0;

        if (students && Array.isArray(students)) {
            students.forEach(s => {
                const rep = studentReportsMap[s.userId] || (s.admissionNo ? studentReportsMap[s.admissionNo] : null);
                if (rep) {
                    if (rep.status === 'SUCCESS') generated++;
                    else if (rep.status === 'GENERATING') generating++;
                    else if (rep.status === 'WARNING') warning++;
                    else if (rep.status === 'FAIL') failed++;
                }
            });
        }

        const notGenerated = Math.max(0, total - (generated + generating + warning + failed));

        return {
            total,
            generated,
            generating,
            warning,
            failed,
            notGenerated
        };
    }, [students, studentReportsMap]);

    const isClassDataLoading = isStudentsLoading || isLoadingStudentReportsList;
    const reportContent = studentReport?.report;

    // Inside the dashboard the shell gives the page its padding; the public /student-report link needs its own
    const isInsideDashboard = Boolean(routeParams.schoolId);
    const classLabel = selectedClass ? `${getClassName(schoolId, selectedClass)} ${selectedSec}` : '';

    const renderStudentReport = () => {
        if (isLoadingStudentReport) {
            return (
                <Card>
                    <LoadingMessage message={`Loading ${selectedStudent.name || 'the student'}’s report…`} />
                </Card>
            );
        }

        if (!studentReport || studentReport.status === 'NOT_GENERATED') {
            return (
                <Card>
                    <EmptyState
                        icon={<LuFileX />}
                        title="No report yet"
                        message={`A report has not been made for ${selectedStudent.name || 'this student'}${academicYear ? ` in ${academicYear}` : ''}.`}
                    />
                </Card>
            );
        }

        return (
            <div className="flex flex-col gap-5">
                <StudentReportHeader
                    report={studentReport}
                    onRefresh={() => getStudentReport({
                        schoolId,
                        userId: selectedStudent.userId,
                        admissionNo: selectedStudent.admissionNo,
                        Class: selectedClass,
                        sec: selectedSec,
                        academicYear
                    })}
                    isRefreshing={isLoadingStudentReport}
                />

                <StudentReportWarningBanner status={studentReport.status} error={studentReport.error} />

                {reportContent?.overallSummary && <StudentReportSummaryCard summary={reportContent.overallSummary} />}

                {reportContent?.performanceOverview && (
                    <StudentReportPerformanceOverview performanceOverview={reportContent.performanceOverview} />
                )}

                {reportContent?.subjectPerformance && reportContent.subjectPerformance.length > 0 && (
                    <StudentReportSubjectsList subjects={reportContent.subjectPerformance} onSelectSubject={handleSelectSubject} />
                )}

                {reportContent?.generalRecommendations && (
                    <StudentReportRecommendationsCard recommendations={reportContent.generalRecommendations} />
                )}
            </div>
        );
    };

    const renderClassList = () => {
        if (!selectedClass || !selectedSec) {
            return (
                <Card>
                    <EmptyState
                        icon={<LuGraduationCap />}
                        title="Choose a class and section"
                        message="Pick a class and a section above to see its students and whether their reports are ready."
                    />
                </Card>
            );
        }

        if (isClassDataLoading) {
            return (
                <Card>
                    <LoadingMessage message={`Loading class ${classLabel}…`} />
                </Card>
            );
        }

        const statusCounts = [
            { label: 'Students', value: stats.total, tone: 'text-ui-ink' },
            { label: 'Generated', value: stats.generated, tone: 'text-emerald-700 dark:text-emerald-300' },
            { label: 'Generating', value: stats.generating, tone: 'text-blue-700 dark:text-blue-300' },
            { label: 'Failed', value: stats.failed, tone: 'text-red-700 dark:text-red-300' },
        ];

        return (
            <>
                {studentReportsListError && (
                    <Notice tone="error" title="Could not load the report statuses" className="mb-5">
                        {studentReportsListError}
                    </Notice>
                )}

                <Card
                    title={`Class ${classLabel}`}
                    headerAction={
                        <SearchInput
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder="Filter by name or admission no."
                            className="w-full sm:w-72"
                        />
                    }
                >
                    <dl className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {statusCounts.map((count) => (
                            <div key={count.label} className="rounded-lg bg-ui-muted px-4 py-3">
                                <dt className="text-xs font-medium text-ui-ink-3">{count.label}</dt>
                                <dd className={`mt-0.5 text-xl font-semibold tabular-nums ${count.tone}`}>{count.value}</dd>
                            </div>
                        ))}
                    </dl>

                    {filteredStudents.length > 0 ? (
                        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                            {filteredStudents.map((student, studentIndex) => {
                                const studentReportData = studentReportsMap[student.userId] ||
                                    (student.admissionNo ? studentReportsMap[student.admissionNo] : null);

                                return (
                                    <StudentReportCard
                                        key={student.userId || studentIndex}
                                        student={student}
                                        report={studentReportData}
                                        onSelect={handleSelectStudent}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <EmptyState
                            icon={<LuUsers />}
                            title="No students found"
                            message={searchQuery
                                ? `No student in class ${classLabel} matches “${searchQuery}”.`
                                : `There are no students in class ${classLabel}.`}
                        />
                    )}
                </Card>
            </>
        );
    };

    const page = (
        <div className="mx-auto max-w-6xl">
            {selectedStudent ? (
                <>
                    <div className="mb-1.5">
                        <button
                            type="button"
                            onClick={handleBackToList}
                            className="inline-flex items-center gap-1.5 rounded text-[13px] font-medium text-ui-ink-2 transition-colors hover:text-ui-ink"
                        >
                            <LuArrowLeft aria-hidden className="size-4" />
                            Class {classLabel}
                        </button>
                    </div>
                    <PageHeader
                        title={selectedStudent.name || 'Student report'}
                        subtitle={`Adm. no. ${selectedStudent.admissionNo || '—'} · Class ${classLabel}${academicYear ? ` · ${academicYear}` : ''}`}
                    />
                    {renderStudentReport()}
                </>
            ) : (
                <>
                    <PageHeader
                        title="Student reports"
                        subtitle={`Each student’s performance report${academicYear ? ` for ${academicYear}` : ''}. Choose a class to begin.`}
                    />

                    <Card className="mb-5">
                        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
                            <Field label="Class">
                                <Select id="class-select" value={selectedClass} onChange={handleClassChange} disabled={isClassesLoading}>
                                    <option value="" disabled>{isClassesLoading ? 'Loading classes…' : 'Select class'}</option>
                                    {classes && classes.map((classItem, classIndex) => (
                                        <option key={classIndex} value={classItem.Class}>
                                            {getClassName(schoolId, classItem.Class)}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                            <Field label="Section">
                                <Select id="section-select" value={selectedSec} onChange={handleSectionChange} disabled={!selectedClass}>
                                    <option value="" disabled>{!selectedClass ? 'Select class first' : 'Select section'}</option>
                                    {availableSections.map((section, sectionIndex) => (
                                        <option key={sectionIndex} value={section}>
                                            {section}
                                        </option>
                                    ))}
                                </Select>
                            </Field>
                            <Button
                                variant="secondary"
                                onClick={handleRefreshClassData}
                                disabled={!selectedClass || !selectedSec}
                                loading={isClassDataLoading}
                                leftIcon={<LuRefreshCw className="size-4" />}
                            >
                                Refresh
                            </Button>
                        </div>
                    </Card>

                    {renderClassList()}
                </>
            )}
        </div>
    );

    if (isInsideDashboard) {
        return page;
    }

    return <div className="min-h-screen bg-ui-page px-8 py-6 text-ui-ink">{page}</div>;
}
